import { useEffect, useState, type FunctionComponent } from "react";
import { Link, Outlet, useParams } from "react-router-dom";
import Spinner from "../../../components/spinner.components";
import {
	CampaignStatus,
	type ICampaign,
} from "../../../types/models/campaign.models.types";
import type { ICampaignMetrics } from "../../../types/models/campaign-metrics.types";
import { SocialPlatforms } from "../../../types/models/social.models.types";
import { useToast } from "../../../hooks/use-toast";
import { ActiveCampaignBadge } from "../../../components/badges.components";
import ClockIcon from "../../../assets/svgs/common/clock.svg";
import { Slider } from "../../../components/ui/slider";
import millify from "millify";
import { formatDate, getBarColor, handleHttpError } from "../../../utils/utils";
import AddImageIcon from "../../../assets/svgs/common/add-image.svg";
import { useGetCampaignMetricsAdminHttp } from "../../../hooks/http/admin/campaigns/get-campaign-metrics.admin.http";
import type { ICampaignMetricMinimal } from "../../../types/models/campaign-metric.types";
import StatsHomeDashboardAdminPartial from "../../../partials/admin/home/home/stats-home-dashboard.admin.partials";
import { Linechart } from "../../../components/charts/line.chart";
import { Barchart } from "../../../components/charts/bar.chart";
import MenuIcon from "../../../assets/svgs/common/menu.svg";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import ShareIcon from "../../../assets/svgs/common/share.svg";
import { Button } from "../../../components/ui/button";
import useCampaignStore from "../../../store/campaign.store";

export const CampaignHomeAdminScreen: FunctionComponent = () => {
	const { data: campaignData, fetching: fetchingCampaign } = useCampaignStore();
	const [campaignMetrics, setCampaignMetrics] =
		useState<ICampaignMetricMinimal>();

	const { execute: getCampaignMetricsHttp, fetching: fetchingCampaignMetrics } =
		useGetCampaignMetricsAdminHttp();

	const { campaignId } = useParams();
	const { toast } = useToast();

	async function getCampaignMetrics() {
		if (!campaignId) {
			return;
		}
		const response = await getCampaignMetricsHttp({
			body: null,
			pathParams: { id: campaignId },
		});
		if (!response.success) {
			return handleHttpError(response, toast);
		}
		setCampaignMetrics(response.data.metrics);
	}

	const AverageViewsPerPostChartData = [
		{
			category: "Views",
			value: campaignMetrics?.averageViewsPerPost ?? 0,
			fill: getBarColor(1),
		},
		{
			category: "Likes",
			value: campaignMetrics?.averageLikesPerPost ?? 0,
			fill: getBarColor(2),
		},
		{
			category: "Comments",
			value: campaignMetrics?.averageCommentsPerPost ?? 0,
			fill: getBarColor(0),
		},
		{
			category: "Shares",
			value: campaignMetrics?.averageSharesPerPost ?? 0,
			fill: getBarColor(3),
		},
	];

	const ApprovedPostsChartData = [
		{
			category: "Total Posts",
			value: campaignMetrics?.totalPosts ?? 0,
			fill: getBarColor(0),
			description: "Total number of posts submitted across the campaign.",
		},
		{
			category: "Live",
			value:
				(campaignMetrics?.approvedPosts ?? 0) +
				(campaignMetrics?.flaggedPosts ?? 0),
			fill: getBarColor(1),
			description: `Posts approved and published for the campaign, plus posts flagged for review.\n\nLive: ${campaignMetrics?.approvedPosts ?? 0}\nFlagged: ${campaignMetrics?.flaggedPosts ?? 0}`,
		},
		{
			category: "Drafts",
			value: campaignMetrics?.pendingDraftPosts ?? 0,
			fill: getBarColor(3),
			description: `Posts that are in draft and not yet approved or rejected.\n\nPending Drafts: ${campaignMetrics?.pendingDraftPosts ?? 0}\nApproved: ${campaignMetrics?.draftApprovedPosts ?? 0}`,
		},
		{
			category: "Rejected",
			value:
				(campaignMetrics?.rejectedPosts ?? 0) +
				(campaignMetrics?.disqualifiedPosts ?? 0),
			fill: getBarColor(2),
			description: `Posts rejected and disqualified for the campaign.\n\nRejected Drafts: ${campaignMetrics?.draftRejectedPosts ?? 0}\nRejected Posts: ${campaignMetrics?.postRejectedPosts ?? 0}\nDisqualified: ${campaignMetrics?.disqualifiedPosts ?? 0}`,
		},
	];
	const PostPerDayChartData = [
		{
			category: "Total Posts",
			value: millify(campaignMetrics?.totalPosts ?? 0),
		},
	];

	const TotalViewsChartData = [
		{
			category: "Total views",
			value: millify(campaignMetrics?.totalViews ?? 0),
		},
		{
			category: "Cost per view",
			value: `₹ ${(campaignMetrics?.costPerView ?? 0).toFixed(3)}`,
		},
	];

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		getCampaignMetrics();
	}, [campaignId]);

	const startDate = campaignData?.startDate
		? new Date(campaignData.startDate)
		: null;
	const endDate = campaignData?.endDate ? new Date(campaignData.endDate) : null;

	// youtube campaigns carry their metrics under campaign.youtube.metrics
	const youtubeMetrics = (
		campaignData as ICampaign & {
			youtube?: { metrics: ICampaignMetrics | null } | null;
		}
	)?.youtube?.metrics;
	const budgetUsed =
		(campaignData?.socialPlatform === SocialPlatforms.youtube
			? youtubeMetrics?.budgetUsed
			: campaignData?.metrics?.budgetUsed) || 0;

	return (
		<div className="flex flex-col gap-8 bg-background pt-16 w-full">
			{fetchingCampaign ? (
				<div className="flex justify-center items-center w-full h-96">
					<Spinner />
				</div>
			) : (
				<div className="flex gap-6 min-w-0">
					{campaignData?.coverImage?.url ? (
						<img
							className="rounded-lg w-full max-w-md h-60 object-cover shrink-0"
							src={`${campaignData.coverImage.url}`}
							alt=""
						/>
					) : (
						<div className="flex justify-center items-center border border-border rounded-lg w-full max-w-md h-60 shrink-0">
							<AddImageIcon />
						</div>
					)}

					<div className="flex flex-col justify-between gap-6 w-full min-w-0">
						<div className="flex flex-col gap-4 min-w-0">
							<div className="flex justify-between items-start gap-4 w-full">
								<div className="flex items-center gap-4 flex-wrap min-w-0">
									{campaignData?.campaignStatus.status ===
										CampaignStatus.live && <ActiveCampaignBadge />}

									<div className="flex items-center gap-1 px-4 py-2 border border-border rounded-full">
										<ClockIcon />
										<p
											className="font-semibold text-base"
											// biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
											dangerouslySetInnerHTML={{
												__html:
													startDate && endDate
														? `${formatDate(startDate.toISOString())} - ${formatDate(endDate.toISOString())}`
														: "",
											}}
										/>
									</div>
								</div>

								<div className="flex items-center gap-2 shrink-0">
									<Button
										onClick={() => {
											navigator.clipboard.writeText(
												`${window.location.origin}/campaign-brief/${campaignId}`,
											);
											toast({
												content: "Brief link copied",
												toastType: "success",
											});
										}}
										className="gap-1 bg-tertiarybuttonbg py-6 border border-border font-semibold text-base"
									>
										<ShareIcon />
										Share brief
									</Button>

									<DropdownMenu>
										<DropdownMenuTrigger className="bg-tertiarybuttonbg px-4 py-6 rounded-xl">
											<MenuIcon />
										</DropdownMenuTrigger>
										<DropdownMenuContent
											className="bg-background"
											collisionPadding={34}
											align="end"
										>
											{(() => {
												const menuItems = [
													{
														title: "Release earnings",
														link: `${window.location.pathname}/release-earnings`,
														textClass: "text-approvebuttontext",
													},
													{
														title: "Brand Managers",
														link: `${window.location.pathname}/brand-managers`,
														textClass: "text-previousbuttontext",
													},
													{
														title: "Export analytics",
														link: `${window.location.pathname}/export`,
													},
													{
														title: "Duplicate",
														link: `${window.location.pathname}/duplicate`,
													},
													campaignData.hasFixedPayout && {
														title: "Manage fixed payout",
														link: `${window.location.pathname}/manage-fixed-payout`,
													},
													{
														title: "Link DMS Campaign",
														link: `${window.location.pathname}/link-with-dms-campaign`,
													},
													campaignData?.campaignStatus.status ===
														CampaignStatus.ended && {
														title: "Finalize DMS Cost",
														link: `${window.location.pathname}/finalize-dms-cost`,
													},
													{
														title: "End campaign",
														link: `${window.location.pathname}/end-campaign`,
														textClass: "text-rejectbuttontext",
													},
												].filter(
													(
														item,
													): item is {
														title: string;
														link: string;
														textClass?: string;
													} => !!item,
												);
												return menuItems.map((item, index) => (
													<Link key={item.title} to={item.link}>
														<DropdownMenuItem
															className={`flex justify-end items-center p-3 w-full ${item.textClass ?? ""}`}
															textValue={item.title}
														>
															{item.title}
														</DropdownMenuItem>
														{index < menuItems.length - 1 && (
															<DropdownMenuSeparator className="bg-border" />
														)}
													</Link>
												));
											})()}
										</DropdownMenuContent>
									</DropdownMenu>
								</div>
							</div>

							<h1
								className="font-semibold text-3xl text-primary break-words"
								title={campaignData?.name}
							>
								{campaignData?.name}
							</h1>

							<div className="flex flex-col gap-3 w-full">
								<div className="flex justify-between w-full">
									<div className="flex flex-col justify-between items-start w-full">
										<span className="font-semibold text-base text-secondary">
											Budget used
										</span>
										<span className="font-semibold text-primary text-xl">
											₹ {millify(budgetUsed)} of{" "}
											{millify(campaignData?.budget || 0)}
										</span>
									</div>
									<div className="flex flex-col justify-between items-end w-full">
										<span className="font-semibold text-base text-secondary">
											Client Budget
										</span>
										<p className="font-semibold text-primary text-xl">
											{campaignData?.budgetInternal ? (
												` ₹${campaignData?.budgetInternal.toLocaleString()}`
											) : (
												<Link
													to="add-budget-internal"
													className="text-primarybuttonbg cursor-pointer"
												>
													+ Add Client Budget
												</Link>
											)}
										</p>
									</div>
								</div>

								<Slider
									stepsVisible={false}
									thumbWidth="30"
									thumbHeight="30"
									trackHeight="h-6"
									max={campaignData?.budget || 0}
									value={[budgetUsed]}
								/>
							</div>
						</div>
					</div>
				</div>
			)}
			{fetchingCampaignMetrics ? (
				<div className="flex justify-center items-center w-full h-96">
					<Spinner />
				</div>
			) : (
				campaignMetrics && (
					<div className="flex flex-col gap-8">
						<StatsHomeDashboardAdminPartial
							campaignMetrics={campaignMetrics as ICampaignMetricMinimal}
						/>

						<Barchart
							fetchingCampaignMetrics={fetchingCampaignMetrics}
							campaignMetrics={campaignMetrics as ICampaignMetricMinimal}
							chartData={ApprovedPostsChartData}
							title="Approved posts"
						/>
						<Linechart
							fetchingCampaignMetrics={fetchingCampaignMetrics}
							campaignMetrics={campaignMetrics as ICampaignMetricMinimal}
							lineData={campaignMetrics.postsPerDay}
							chartData={PostPerDayChartData}
							title="Posts per day"
						/>
						<Linechart
							fetchingCampaignMetrics={fetchingCampaignMetrics}
							campaignMetrics={campaignMetrics as ICampaignMetricMinimal}
							lineData={campaignMetrics.totalViewsChart}
							chartData={TotalViewsChartData}
							title="Total views"
						/>
						<Barchart
							fetchingCampaignMetrics={fetchingCampaignMetrics}
							campaignMetrics={campaignMetrics as ICampaignMetricMinimal}
							chartData={AverageViewsPerPostChartData}
							title="Average views per post"
						/>
					</div>
				)
			)}
			{/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
			<div
				className="hidden"
				id="getCampaignMetrics"
				onClick={() => {
					getCampaignMetrics();
				}}
			/>
			<Outlet />
		</div>
	);
};
